import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../components/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  canReadSpeakUpIdeas,
  canReplyToSpeakUpIdea,
  canPrintSpeakUpFlyers,
  isBranchManager,
} from '../../lib/permissions';
import { branchNameById, STATUS_LABEL } from './speakupConstants';
import SpeakUpFlyerCard from './SpeakUpFlyerCard';
import './speakup.css';

// ---------------------------------------------------------------------------
// Gate stage G — what a manager sees.
//
// G1: TWO KINDS OF ITEM, VISIBLY DIFFERENT. Ideas are ordinary rows he can
// answer. A shared concern is tinted, framed, attributed by name and carries
// NO reply control — because D9 and D6 give it different rules, and a manager
// should be able to feel that difference without being taught it.
//
// G2: THE LIMIT IS PRINTED, NOT IMPLIED. "The original report and the person
// who made it are not available to you" stops a manager assuming there is a
// fuller version he could ask for — and stops him asking around the shop for
// it, which is the failure this whole design exists to prevent.
//
// G3: "Reply as <branch> branch manager" is the button label, so the
// role-not-name rule is visible in the act rather than discovered afterwards.
//
// ---------------------------------------------------------------------------
// #478 and #479, Speak Up OOB-6. Four changes, and the first is the defect
// Colby hit on production.
//
// G4: THE HEADING NOW STATES THE SCOPE ACTUALLY APPLIED. It read "Ideas from
// Dallas" for everyone, built from the viewer's own branch — while the
// database returned EVERY branch to an admin or executive. A screen that names
// a scope it is not applying is worse than one with no label at all, and it
// was invisible only because production holds a single idea which happens to
// be Dallas. Colby: "i can reply as the dallas branch manger even though i'm
// not the dallas branch manager."
//
// G5: THE REPLY CONTROL IS DRAWN PER ROW, for a branch manager of THAT row's
// branch. Admins and executives no longer get it anywhere. The idea form
// promises "your branch manager will read this", the answer arrives signed
// "manager", so anybody else sending it makes the form a lie. Colby's
// reasoning, which is the best argument in the whole issue: "if we tell a user
// on their submission that your branch manager will read it i feel like it
// implys your branch manger will also be responding to it."
//
// The handler still answers any idea from any branch, from her own inbox, and
// the reporter now sees that one labelled "Ownership" — a different voice
// rather than a borrowed one.
//
// G6: THE IDEAS TAB IS CONDITIONAL, AND THE PAGE IS NOT. A department manager
// reads no ideas now, but CAN still have a concern summary shared with her
// personally — admin_list_share_recipients() offers the handler every active
// manager and executive. Gating the whole page would have taken away the only
// screen that shows it, silently, after the handler had chosen to send it. So
// the page stays open and the ideas half is what narrows, and the two RPCs are
// loaded independently so a refusal on one cannot blank the other.
//
// G7: THE WALL FLYERS REACH THIS PAGE TOO (#479). They were ONLY in the
// handler's inbox, which #472 narrowed to the assigned handler alone — so the
// only person who could print a poster was the one person whose job is reading
// what comes back, and Colby found it by being unable to follow his own close's
// instruction to scan one. D8 says assigning is not reading; printing a poster
// is not reading either. A branch manager gets their own branch; an admin or
// executive gets all four.
//
// They are NOT moved off the inbox, and that correction is worth recording
// because the obvious fix was wrong: this page is hidden from whoever holds
// the handler entry, so moving them would have left ANGIE — the handler — as
// the only person unable to print the posters for her own programme. Same bug,
// different person. One shared SpeakUpFlyerCard renders on both surfaces.
// ---------------------------------------------------------------------------
export default function SpeakUpManager() {
  const { profile, isAdmin, isManager, isExecutive } = useAuth();
  const auth = { isAdmin, isManager, isExecutive, profile };

  const mayReadIdeas = canReadSpeakUpIdeas(auth);
  const mayPrint = canPrintSpeakUpFlyers(auth);
  // A branch manager who is not also an admin or executive sees one branch,
  // everywhere on this page — the ideas list, the heading and the flyers.
  const ownBranchOnly = !isAdmin && !isExecutive && isBranchManager({ isManager, profile });

  const [tab, setTab] = useState(mayReadIdeas ? 'ideas' : 'shared');
  const [ideas, setIdeas] = useState(mayReadIdeas ? null : []);
  const [shared, setShared] = useState(null);
  const [error, setError] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    // G6: independent, deliberately. manager_list_ideas() RAISES rather than
    // returning an empty list when the caller may not read ideas, and a shared
    // concern must survive that — one Promise.all with a single error branch
    // would have blanked the page for the person the share was meant for.
    const shareRes = await supabase.rpc('get_shared_with_me');
    setShared(shareRes.error ? [] : (shareRes.data || []));

    if (!mayReadIdeas) { setIdeas([]); return; }
    const ideaRes = await supabase.rpc('manager_list_ideas');
    if (ideaRes.error) { setError('load_error'); return; }
    setIdeas(ideaRes.data || []);
  }, [mayReadIdeas]);

  useEffect(() => { load(); }, [load]);

  async function sendReply(id) {
    if (!reply.trim()) return;
    setSending(true);
    const { error: rpcError } = await supabase.rpc('manager_reply_idea', { p_id: id, p_body: reply.trim() });
    setSending(false);
    if (rpcError) { setError('reply_error'); return; }
    setReply('');
    setReplyTo(null);
    load();
  }

  if (error === 'load_error') {
    return (
      <div className="speakup-admin-wrap">
        <div className="card"><div className="card-body">You don't have access to this section.</div></div>
      </div>
    );
  }

  const branchLabel = profile?.branch_id ? branchNameById(profile.branch_id) : 'your branch';

  // G4: the heading describes what this viewer is actually being shown.
  const scopeLine = !mayReadIdeas
    ? 'Anything shared with you'
    : ownBranchOnly
      ? `Ideas from ${branchLabel}, and anything shared with you`
      : 'Ideas from all four branches, and anything shared with you';

  const list = tab === 'ideas' ? ideas : shared;

  return (
    <div className="speakup-admin-wrap">
      <div className="page-header">
        <div className="page-title">Speak Up</div>
        <div className="page-sub">{scopeLine}</div>
      </div>

      {/* G7, #479. Printing a poster is not reading reports.
          The same card renders on the handler's inbox — see SpeakUpFlyerCard,
          which explains why moving it here alone would have locked Angie out
          of the posters for the programme she runs. */}
      {mayPrint && (
        <SpeakUpFlyerCard
          branchIds={ownBranchOnly && profile?.branch_id != null ? [Number(profile.branch_id)] : null}
        />
      )}

      <div className="speakup-tabs">
        {/* G6: no ideas tab at all for a department manager, rather than a tab
            that opens onto a refusal. */}
        {mayReadIdeas && (
          <button
            className={tab === 'ideas' ? 'speakup-tab speakup-tab-active' : 'speakup-tab'}
            onClick={() => setTab('ideas')}
            type="button"
          >
            {ownBranchOnly ? `${branchLabel} ideas` : 'All ideas'} · {(ideas || []).length}
          </button>
        )}
        <button
          className={tab === 'shared' ? 'speakup-tab speakup-tab-active' : 'speakup-tab'}
          onClick={() => setTab('shared')}
          type="button"
        >
          Shared with you · {(shared || []).length}
        </button>
      </div>

      <div className="card">
        {list === null && <div className="card-body">Loading…</div>}
        {list && list.length === 0 && (
          <div className="card-body text-muted">
            {tab === 'ideas'
              ? (ownBranchOnly ? 'No ideas from your branch yet.' : 'No ideas yet.')
              : 'Nothing has been shared with you.'}
          </div>
        )}

        {/* Ideas — ordinary rows, read as written, answerable. */}
        {tab === 'ideas' && (ideas || []).map((row) => {
          // G5, #478. Asked per ROW, not once per page. An admin reading every
          // branch gets no reply control on any of them; a branch manager gets
          // it on their own branch and nowhere else. The database enforces the
          // same rule in manager_reply_idea(), so this only decides whether a
          // button is drawn — a greyed-out control is advisory, and anyone
          // holding the submission id could otherwise go around the screen.
          const mayReply = canReplyToSpeakUpIdea(auth, row.branch_id);
          return (
            <div key={row.id} className="speakup-row">
              <div className="speakup-row-top">
                <span className="speakup-chip speakup-chip-cat">{row.category}</span>
                <strong>{branchNameById(row.branch_id)}</strong>
                <span className="speakup-chip speakup-chip-status">{STATUS_LABEL[row.status] || row.status}</span>
                <span className="speakup-when">
                  {new Date(row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </div>
              <div className="speakup-row-body">{row.body}</div>

              {!row.has_thread ? (
                <div className="speakup-row-meta">
                  One-time submission — no way to reply
                </div>
              ) : mayReply ? (
                replyTo === row.id ? (
                  <div className="speakup-reply-row" style={{ marginTop: 10 }}>
                    <input
                      className="speakup-input"
                      placeholder="Reply…"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                    />
                    <button
                      className="speakup-btn-sm speakup-btn-dark"
                      type="button"
                      disabled={sending}
                      onClick={() => sendReply(row.id)}
                    >
                      {sending ? '…' : 'Send'}
                    </button>
                  </div>
                ) : (
                  /* G3: the role is in the button label, so role-not-name is
                     visible in the act rather than discovered afterwards. */
                  <button
                    className="speakup-btn-sm"
                    type="button"
                    style={{ marginTop: 10 }}
                    onClick={() => { setReplyTo(row.id); setReply(''); }}
                  >
                    Reply as {branchNameById(row.branch_id)} branch manager
                  </button>
                )
              ) : (
                /* G5. Say WHY there is no button, rather than leaving a row
                   that looks broken. An admin reading every branch needs to
                   know this is a rule and not a missing feature — and the
                   sentence names who does answer, so nothing looks unattended.
                   The reporter was promised their branch manager; this is the
                   screen keeping that promise rather than describing it. */
                <div className="speakup-row-meta">
                  Answered by {branchNameById(row.branch_id)}'s branch manager, or by ownership from the Speak Up inbox
                </div>
              )}
            </div>
          );
        })}

        {/* G1: a shared concern is tinted, framed, attributed by name, and
            carries no reply control at all. */}
        {tab === 'shared' && (shared || []).map((row) => (
          <div key={row.share_id} className="speakup-row speakup-row-shared-item">
            <div className="speakup-row-top">
              <span className="speakup-chip speakup-chip-cat">{row.category}</span>
              <strong>{branchNameById(row.branch_id)}</strong>
              <span className="speakup-when">
                {new Date(row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            </div>
            <div className="speakup-shared-from">Summary from {row.shared_by_name}:</div>
            <div className="speakup-row-body">{row.summary}</div>
            {/* G2: the limit is PRINTED. A manager who assumes a fuller
                version exists goes looking for it around the shop, which is
                the failure this whole design exists to prevent. */}
            <div className="speakup-shared-limit">
              You are seeing a summary. The original report and the person who made it are not
              available to you.
            </div>
          </div>
        ))}
      </div>

      {error === 'reply_error' && <div className="speakup-error">Could not send that. Try again.</div>}
    </div>
  );
}
